import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlag } from 'flags';
import * as yup from 'yup';
import { Formik, Field, Form } from 'formik';

import { Card } from 'components/Card/Card';
import Button from 'components/Button/Button';
import Tooltip from 'components/Tooltip/Tooltip';
import Loading from 'components/Loading/Loading';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import colors from 'theme/Colors.module.scss';
import './MyUserDetail.scss';

import {
  Box,
  Button as RegularButton,
  Text,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  FormLabel,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';

import {
  userFieldValidations,
  userPasswordFieldValidations,
} from 'modules/users/userValidations';

import {
  useGetUserByIdQuery,
  useUpdateUserByIdMutation,
} from 'modules/users/usersApi';

const defaultValues = {
  company_id: '',
  email: '',
  firstname: '',
  id: '',
  lastname: '',
  source: '',
  password: '',
  password_confirmation: '',
};

const schema = yup.object().shape({
  ...userFieldValidations,
  ...userPasswordFieldValidations,
});

export default ({ match }) => {
  const dispatch = useDispatch();

  const isReadOnly = useFlag(['features', 'isReadOnly']);
  const sessionUser = useSelector(state => state.app.user);
  const { id } = sessionUser;
  const [showPassword, setShowPassword] = useState(false);

  const {
    data: userData,
    error,
    isLoading,
  } = useGetUserByIdQuery(id, {
    skip: !id, // Skip the query if companyId is null or undefined
  });

  const [updateUserById] = useUpdateUserByIdMutation();

  const handleSubmit = async (values, form) => {
    form.setSubmitting(true);

    try {
      await updateUserById({ id, data: values });
    } catch (err) {
      console.error(err);
    } finally {
      form.setSubmitting(false);
    }
  };

  if (error) {
    dispatch(
      pushNotificationWithTimeout({
        type: 'error',
        message: 'Failed to update user data',
      })
    );
  }

  if (isLoading) {
    return <Loading type="fullpage" />;
  }

  return (
    <>
      <Box className="my-user-header">
        <Box className="my-user-header__left">
          <Button
            type="button"
            url="/users"
            icon="back"
            text="Back"
            textColor={colors.slate}
            btnStyle="transparent"
          />
        </Box>
        <Box className="my-user-header__nav">
          <Text as="p" fontWeight="600">
            My Account
          </Text>
        </Box>
        <Box className="my-user-header__right"></Box>
      </Box>
      {userData ? (
        <Formik
          enableReinitialize
          validateOnMount
          initialValues={{ ...defaultValues, ...userData }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {form => {
            const { isSubmitting, dirty } = form;

            return (
              <Form className="my-user-form__container">
                <Card className="my-user-form__card">
                  <Text as="p" fontWeight="500">
                    User Information
                  </Text>
                  <Flex flexDirection="column" gap={8} mt={8}>
                    <Flex gap={8} flexWrap="wrap">
                      <Flex flexDirection="column" flex="1 1 300px" minW="0">
                        <Field name="firstname">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.firstname && form.touched.firstname
                              }
                            >
                              <FormLabel>First Name</FormLabel>
                              <Input
                                {...field}
                                placeholder="First Name*"
                                focusBorderColor={colors.primary600}
                                disabled={isReadOnly || isSubmitting}
                              />
                              <FormErrorMessage>
                                {form.errors.firstname}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </Flex>
                      <Flex flexDirection="column" flex="1 1 300px" minW="0">
                        <Field name="lastname">
                          {({ field, form }) => (
                            <FormControl
                              isInvalid={
                                form.errors.lastname && form.touched.lastname
                              }
                            >
                              <FormLabel>Last Name</FormLabel>
                              <Input
                                {...field}
                                placeholder="Last Name*"
                                focusBorderColor={colors.primary600}
                                disabled={isReadOnly || isSubmitting}
                              />
                              <FormErrorMessage>
                                {form.errors.lastname}
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                      </Flex>
                    </Flex>

                    <hr
                      style={{
                        marginTop: '0',
                        marginBottom: '-1rem',
                      }}
                    />

                    <Flex flexDirection="column">
                      <Field name="password">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.password && form.touched.password
                            }
                          >
                            <FormLabel>Password*</FormLabel>
                            <InputGroup size="md">
                              <Input
                                pr="4.5rem"
                                {...field}
                                placeholder="Password*"
                                focusBorderColor={colors.primary600}
                                disabled={isReadOnly || isSubmitting}
                                type={showPassword ? 'text' : 'password'}
                              />
                              <InputRightElement width="4.5rem">
                                <RegularButton
                                  h="1.75rem"
                                  size="sm"
                                  onClick={() => {
                                    setShowPassword(!showPassword);
                                  }}
                                >
                                  {showPassword ? (
                                    <i className="fas fa-eye-slash" />
                                  ) : (
                                    <i className="fas fa-eye" />
                                  )}
                                </RegularButton>
                              </InputRightElement>
                            </InputGroup>
                            <FormErrorMessage>
                              {form.errors.password}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>

                    <Flex flexDirection="column">
                      <Field name="password_confirmation">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={
                              form.errors.password_confirmation &&
                              form.touched.password_confirmation
                            }
                          >
                            <FormLabel>Password Confirmation*</FormLabel>
                            <InputGroup size="md">
                              <Input
                                pr="4.5rem"
                                {...field}
                                placeholder="Password Confirmation*"
                                focusBorderColor={colors.primary600}
                                disabled={isReadOnly || isSubmitting}
                                type={showPassword ? 'text' : 'password'}
                              />
                              <InputRightElement width="4.5rem">
                                <RegularButton
                                  h="1.75rem"
                                  size="sm"
                                  onClick={() => {
                                    setShowPassword(!showPassword);
                                  }}
                                >
                                  {showPassword ? (
                                    <i className="fas fa-eye-slash" />
                                  ) : (
                                    <i className="fas fa-eye" />
                                  )}
                                </RegularButton>
                              </InputRightElement>
                            </InputGroup>
                            <FormErrorMessage>
                              {form.errors.password_confirmation}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>

                    <hr
                      style={{
                        marginTop: '0',
                        marginBottom: '-1rem',
                      }}
                    />

                    <Flex flexDirection="column">
                      <Field name="email">
                        {({ field, form }) => (
                          <FormControl
                            isInvalid={form.errors.email && form.touched.email}
                          >
                            <FormLabel>
                              Email&nbsp;
                              <Tooltip
                                tipText="Updating your email address affects the login credentials."
                                direction="right"
                              />
                            </FormLabel>
                            <Input
                              {...field}
                              type="email"
                              placeholder="Email*"
                              focusBorderColor={colors.primary600}
                              disabled={isReadOnly || isSubmitting}
                            />
                            <FormErrorMessage>
                              {form.errors.email}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    </Flex>
                  </Flex>
                </Card>

                {!isReadOnly && (
                  <Flex flexDirection="column" align="center">
                    <Button
                      type="submit"
                      text="Update User"
                      textColor={colors.white}
                      btnStyle="solid"
                      hoverColor={colors.primary500}
                      textHoverColor={colors.white}
                      disabled={isReadOnly || isSubmitting || !dirty}
                    />
                    <Text as="p" className="footnote">
                      An asterisk (*) marks a required field.
                    </Text>
                  </Flex>
                )}
              </Form>
            );
          }}
        </Formik>
      ) : (
        'Not found'
      )}
    </>
  );
};
